import { findItemByBarcodeInternal } from './inventoryService';
import { InventoryItem } from '../types';
import { isUserAuthenticated } from './firebase';
import { validateDataMatrix } from '../utils/barcodeUtils'; // Import new validation function

// Re-export for use
export const findItemByBarcode = findItemByBarcodeInternal;

// Types for the barcode service listeners
export interface BarcodeListenerCallbacks {
  onItemFound: (item: InventoryItem) => void;
  onNewItemBarcode: (barcode: string) => void;
  onError?: (error: Error) => void;
}

// New type for global scan listener
export type GlobalBarcodeScanCallback = (barcode: string) => void;
const globalScanListeners = new Set<GlobalBarcodeScanCallback>();

/**
 * Enum for barcode input target types
 */
export enum BarcodeInputTargetType {
  FORM_FIELD = 'form_field',
  SEARCH = 'search',
  TABLE = 'table'
}

// Default timeout in ms for barcode input (time between keystrokes)
const DEFAULT_TIMEOUT = 50;

// Service state
let isListening = false;
let barcodeParts: string[] = [];
let keypressTimeout: number | null = null;
let currentCallbacks: BarcodeListenerCallbacks | null = null;
let keypressActiveForGlobal = false; // To control when keypresses are globally processed

// For context management
interface BarcodeFormField {
  id: string;
  formId: string;
  type: BarcodeInputTargetType;
}

interface BarcodeContext {
  id: string;
  type: BarcodeInputTargetType;
  formFields?: BarcodeFormField[];
}

// Store registered contexts
const registeredContexts: Record<string, BarcodeContext> = {};
let activeContextId: string | null = null;

/**
 * Initialize barcode scanner listener implementation
 */
const _initializeBarcodeListener = (callbacks: BarcodeListenerCallbacks): (() => void) => {
  if (isListening) {
    stopBarcodeListener();
  }

  isListening = true;
  currentCallbacks = callbacks;
  barcodeParts = [];

  const handleKeyPress = async (event: KeyboardEvent) => {
    // Skip if meta, control or alt keys are pressed (likely a keyboard shortcut)
    if (event.metaKey || event.ctrlKey || event.altKey) {
      return;
    }

    // Handle Enter key to complete barcode input
    if (event.key === 'Enter' && barcodeParts.length > 0) {
      event.preventDefault();
      
      // Get the complete barcode
      const barcode = barcodeParts.join('');
      barcodeParts = [];
      
      // Process the barcode
      await processBarcode(barcode);
      return;
    }

    // For regular keypress, add to barcode buffer and reset timeout
    if (keypressTimeout !== null) {
      window.clearTimeout(keypressTimeout);
    }

    // Add keypress to barcode
    if (event.key.length === 1) {
      barcodeParts.push(event.key);
    }

    // Set timeout to clear buffer if keyboard input is slow (indicating manual typing)
    keypressTimeout = window.setTimeout(() => {
      barcodeParts = [];
      keypressTimeout = null;
    }, DEFAULT_TIMEOUT);
  };

  // Add listener to window
  window.addEventListener('keydown', handleKeyPress);

  // Return cleanup function
  return () => {
    window.removeEventListener('keydown', handleKeyPress);
    isListening = false;
    currentCallbacks = null;
    barcodeParts = [];
    if (keypressTimeout !== null) {
      window.clearTimeout(keypressTimeout);
      keypressTimeout = null;
    }
  };
};

/**
 * Process a scanned barcode
 */
const processBarcode = async (barcode: string): Promise<void> => {
  // Notify global listeners first if active
  if (keypressActiveForGlobal && globalScanListeners.size > 0) {
    // Validate for global listeners too if needed
    if (!validateDataMatrix(barcode)) {
      console.warn(`Global scan: Invalid barcode format: ${barcode}`);
      return;
    }
    globalScanListeners.forEach(listener => listener(barcode));
  }

  if (!currentCallbacks || !isListening || !barcode) {
    // If no form-specific callback, and it wasn't handled globally, just return
    if (!keypressActiveForGlobal || globalScanListeners.size === 0) {
        return;
    }
  }
  
  // Proceed with form-specific logic if callbacks are present
  if (currentCallbacks && isListening) {
    try {
      // Validate the barcode format using Data Matrix rules
      if (!validateDataMatrix(barcode)) {
        console.warn(`Form scan: Invalid barcode format: ${barcode}`);
        if (currentCallbacks?.onError) {
          currentCallbacks.onError(new Error(`Geçersiz barkod formatı: ${barcode}`));
        }
        return;
      }
      
      const item = await findItemByBarcode(barcode);
      
      if (item) {
        currentCallbacks.onItemFound(item);
      } else {
        currentCallbacks.onNewItemBarcode(barcode);
      }
    } catch (error) {
      console.error('Error processing barcode for form:', error);
      if (currentCallbacks.onError && error instanceof Error) {
        currentCallbacks.onError(error);
      }
    }
  }
};

/**
 * Stop barcode scanner listener
 */
export const stopBarcodeListener = (): void => {
  isListening = false;
  currentCallbacks = null;
  barcodeParts = [];
  if (keypressTimeout !== null) {
    window.clearTimeout(keypressTimeout);
    keypressTimeout = null;
  }
};

/**
 * Check if barcode listener is currently active
 */
export const isBarcodeListenerActive = (): boolean => {
  return isListening;
};

/**
 * Initialize barcode scanner listener with authentication check
 */
export const initializeBarcodeListener = (callbacks: BarcodeListenerCallbacks): (() => void) => {
  // Check authentication before proceeding
  if (!isUserAuthenticated()) {
    const authError = new Error('User is not authenticated. Please log in to use the barcode scanner.');
    if (callbacks.onError) {
      callbacks.onError(authError);
    } else {
      console.error(authError);
    }
    return () => {}; // Return empty cleanup function
  }
  
  // If authenticated, initialize the listener
  return _initializeBarcodeListener(callbacks);
};

/**
 * Register a barcode scanner context
 */
export const registerBarcodeContext = (context: BarcodeContext): (() => void) => {
  registeredContexts[context.id] = context;
  
  // Return function to unregister
  return () => {
    delete registeredContexts[context.id];
    if (activeContextId === context.id) {
      activeContextId = null;
    }
  };
};

/**
 * Set active barcode context
 */
export const setActiveBarcodeContext = (contextId: string): boolean => {
  if (registeredContexts[contextId]) {
    activeContextId = contextId;
    return true;
  }
  return false;
};

/**
 * Register a global barcode scan listener
 */
export const addGlobalBarcodeScanListener = (callback: GlobalBarcodeScanCallback): (() => void) => {
  globalScanListeners.add(callback);
  return () => {
    globalScanListeners.delete(callback);
  };
};

/**
 * Activate or deactivate global keypress listening for barcode scanning.
 * This is separate from form-specific listeners.
 */
export const setGlobalBarcodeListeningStatus = (active: boolean): void => {
  keypressActiveForGlobal = active;
};

// Global keypress handler for general barcode input
let globalKeypressCleanup: (() => void) | null = null;

export const initializeGlobalKeypressListener = (): (() => void) => {
  if (globalKeypressCleanup) {
    globalKeypressCleanup(); // Clean up existing listener
  }

  const handleGlobalKeyPress = async (event: KeyboardEvent) => {
    if (!keypressActiveForGlobal) return; // Only process if global listening is active

    if (event.metaKey || event.ctrlKey || event.altKey) return;

    if (event.key === 'Enter' && barcodeParts.length > 0) {
      event.preventDefault();
      const barcode = barcodeParts.join('');
      barcodeParts = [];
      await processBarcode(barcode); // processBarcode will notify global listeners
      return;
    }

    if (keypressTimeout !== null) window.clearTimeout(keypressTimeout);
    
    if (event.key.length === 1) barcodeParts.push(event.key);

    keypressTimeout = window.setTimeout(() => {
      barcodeParts = [];
      keypressTimeout = null;
    }, DEFAULT_TIMEOUT);
  };

  window.addEventListener('keydown', handleGlobalKeyPress);
  globalKeypressCleanup = () => {
    window.removeEventListener('keydown', handleGlobalKeyPress);
  };
  return globalKeypressCleanup;
}; 